import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faBars,
	faCalendar,
	faCaretSquareDown,
	faCircle,
	faClock,
	faDollarSign,
	faEllipsis,
	faEnvelope,
	faFileAlt,
	faFont,
	faHashtag,
	faIcons,
	faImage,
	faLink,
	faPercent,
	faPhone,
	faPuzzlePiece,
	faSquare,
	faStar,
	faTag,
	faTags,
	faThumbtack,
	faVolumeUp,
} from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { useComponentRegistry } from '../../../registry/ComponentRegistry';
import type { DisplayStyle, FieldConfiguration } from '../../../types';
import { FieldPreview } from './FieldPreview';

interface DisplayStyleSelectorProps {
	field: FieldConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	previewValue?: string;
	configuration?: any;
}

// Display style options with icons
const displayStyles: Array<{
	value: DisplayStyle;
	label: string;
	icon: IconDefinition;
}> = [
	{ value: 'text', label: 'Text', icon: faFont },
	{ value: 'badge', label: 'Badge', icon: faTag },
	{ value: 'badge_list', label: 'Badge List', icon: faTags },
	{ value: 'pill', label: 'Pill', icon: faEllipsis },
	{ value: 'chip', label: 'Chip', icon: faTag },
	{ value: 'dot', label: 'Dot', icon: faCircle },
	{ value: 'icon', label: 'Icon', icon: faIcons },
	{ value: 'icon_text', label: 'Icon + Text', icon: faFileAlt },
	{ value: 'phone', label: 'Phone', icon: faPhone },
	{ value: 'email', label: 'Email', icon: faEnvelope },
	{ value: 'date', label: 'Date', icon: faCalendar },
	{ value: 'relative_date', label: 'Relative Date', icon: faClock },
	{ value: 'number', label: 'Number', icon: faHashtag },
	{ value: 'currency', label: 'Currency', icon: faDollarSign },
	{ value: 'percent', label: 'Percent', icon: faPercent },
	{ value: 'image', label: 'Image', icon: faImage },
	{ value: 'button', label: 'Button', icon: faSquare },
	{ value: 'audio_player', label: 'Audio Player', icon: faVolumeUp },
	{
		value: 'actions_dropdown',
		label: 'Actions Dropdown',
		icon: faCaretSquareDown,
	},
	{ value: 'related_entity_list', label: 'Related Entities', icon: faLink },
	{ value: 'pin_toggle', label: 'Pin Toggle', icon: faThumbtack },
	{
		value: 'primary_contact_toggle',
		label: 'Primary Contact Toggle',
		icon: faStar,
	},
	{ value: 'line_break', label: 'Line Break', icon: faBars },
];

export function DisplayStyleSelector({
	field,
	onUpdateField,
	previewValue,
	configuration,
}: DisplayStyleSelectorProps) {
	const colors = useThemeColors();
	const { components: customComponents, componentsByCategory } =
		useComponentRegistry();

	// Sort category names alphabetically, but pin "Other" to the end so the
	// catch-all bucket doesn't break the A→Z scan in the middle of the picker.
	const sortedCategories = Object.keys(componentsByCategory).sort((a, b) => {
		if (a === 'Other' && b !== 'Other') return 1;
		if (b === 'Other' && a !== 'Other') return -1;
		return a.localeCompare(b);
	});

	const handleStyleSelect = (style: (typeof displayStyles)[0]) => {
		if (style.value === 'line_break') {
			// For line breaks, clear data and style configs
			onUpdateField({
				displayStyle: style.value,
				dataConfig: {
					type: 'custom_text',
					customText: '',
				},
				styleConfig: {
					size: 'md',
					colorRule: {
						type: 'static',
						staticColor: {
							bg: 'transparent',
							text: colors.values.textSecondary,
						},
					},
				},
			});
		} else if (
			style.value === 'number' ||
			style.value === 'currency' ||
			style.value === 'percent'
		) {
			// Currency defaults to 'decimal' (fixed 2 decimal places),
			// number and percent default to 'auto' (smart formatting)
			const defaultFormat = style.value === 'currency' ? 'decimal' : 'auto';
			onUpdateField({
				displayStyle: style.value,
				styleConfig: {
					// size default matches the line_break branch above for
					// fields whose config predates styleConfig.
					size: 'md',
					...field.styleConfig,
					formatOptions: {
						...field.styleConfig?.formatOptions,
						numberFormat:
							field.styleConfig?.formatOptions?.numberFormat ?? defaultFormat,
					},
				},
			});
		} else {
			onUpdateField({ displayStyle: style.value });
		}
	};

	return (
		<Card className="mb-4">
			<div className="p-4">
				<Text className="text-base font-semibold mb-3">Display Style</Text>

				<div className="flex flex-wrap gap-2">
					{displayStyles.map((style) => (
						<button
							key={style.value}
							onClick={() => {
								handleStyleSelect(style);
							}}
							className={cn(
								'flex items-center px-3 py-2 border-2 rounded-lg min-w-[100px]',
								field.displayStyle === style.value
									? cn(
											colors.classes.controlSelected,
											colors.classes.borderPrimary,
										)
									: cn(
											colors.classes.bgPrimary,
											colors.classes.borderDefault,
											colors.classes.textSecondary,
											colors.classes.bgPrimaryHover,
										),
							)}
						>
							<Icon
								icon={style.icon}
								size="sm"
								color={
									field.displayStyle === style.value
										? colors.values.primaryDark
										: colors.values.textSecondary
								}
								className="mr-1.5"
							/>
							<Text
								className={cn(
									'text-xs',
									field.displayStyle === style.value
										? 'font-semibold'
										: 'font-medium',
								)}
							>
								{style.label}
							</Text>
						</button>
					))}

					{/* Custom Components Section — grouped by category, A→Z within each
					    group. Built-in styles above stay in their hand-curated order.
					    Each category lives inside its own block-level wrapper with a
					    nested `flex flex-wrap` row so long category lists (e.g. CLIENT
					    HISTORY) wrap inside the modal instead of overflowing it. The
					    previous structure relied on inline `<Text>` headers with
					    `w-full` to force flex line breaks; on narrower modal widths
					    that produced a horizontal scrollbar because adjacent buttons
					    couldn't reflow across category boundaries. */}
					{customComponents.length > 0 && (
						// `min-w-0` here is load-bearing: as a flex child of the outer
						// `flex flex-wrap` above, this wrapper would otherwise inherit
						// `min-width: auto`, which in flex resolves to the wrapper's
						// min-content — i.e. the widest non-wrappable thing inside.
						// The buttons further down have `min-w-[100px]` and won't
						// shrink, so without `min-w-0` the wrapper insists on being
						// wide enough to lay them all out un-wrapped, pushing the
						// modal content past its container and triggering a horizontal
						// scrollbar. `min-w-0` lets it shrink to its row's width so
						// the inner `flex flex-wrap` rows can actually wrap.
						<div className="w-full min-w-0 mt-2">
							<div
								className={cn('w-full h-px mb-2', colors.classes.borderDefault)}
							/>
							<Text
								as="div"
								className="text-xs font-semibold mb-2"
								color="secondary"
							>
								Custom Components
							</Text>
							{sortedCategories.map((category) => {
								const componentsInCategory = [
									...(componentsByCategory[category] ?? []),
								].sort((a, b) => a.displayName.localeCompare(b.displayName));
								return (
									<div key={category} className="w-full min-w-0 mt-3">
										<Text
											as="div"
											className="text-[10px] font-semibold uppercase tracking-wide mb-1 opacity-70"
											color="secondary"
										>
											{category}
										</Text>
										<div className="flex flex-wrap gap-2 min-w-0">
											{componentsInCategory.map((component) => (
												<button
													key={component.id}
													onClick={() => {
														onUpdateField({
															displayStyle: 'custom' as DisplayStyle,
															customComponent: {
																id: component.id,
															},
														});
													}}
													className={cn(
														'flex items-center px-3 py-2 border-2 rounded-lg min-w-[100px]',
														field.displayStyle === 'custom' &&
															field.customComponent?.id === component.id
															? cn(
																	colors.classes.controlSelected,
																	colors.classes.borderPrimary,
																)
															: cn(
																	colors.classes.bgPrimary,
																	colors.classes.borderDefault,
																	colors.classes.textSecondary,
																	colors.classes.bgPrimaryHover,
																),
													)}
												>
													<Icon
														icon={component.icon ?? faPuzzlePiece}
														size="sm"
														color={
															field.displayStyle === 'custom' &&
															field.customComponent?.id === component.id
																? colors.values.primaryDark
																: colors.values.textSecondary
														}
														className="mr-1.5"
													/>
													<Text
														className={cn(
															'text-xs',
															field.displayStyle === 'custom' &&
																field.customComponent?.id === component.id
																? 'font-semibold'
																: 'font-medium',
														)}
													>
														{component.displayName}
													</Text>
												</button>
											))}
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Live Preview */}
				{field.displayStyle !== 'line_break' && (
					<FieldPreview
						field={field}
						previewValue={previewValue}
						configuration={configuration}
					/>
				)}
			</div>
		</Card>
	);
}
